import { useEffect, useState, useCallback, useMemo } from 'react';
import { Card, Space, Row, Col, Typography, Table, Tabs, Slider, Grid } from 'antd';
import { useDispatch, useSelector } from "react-redux";
import Button from '@mui/material/Button';
import { fetchTimeSeriesData, fetchBlendedTimeseriesV2 } from "../../../api/timeseriesApi";
import { selectTimeSeriesData } from "../../../store/slices/timeseries.slice";
import StockChart from "../../../charts/StockChart";
import { useTickerSearchIndex } from "../../../modules/Search/useTickerSearchIndex";
import TickerChip from "../../../components/TickerChip";
import { stringMixin } from "../../../utils/strings";
import { debounce } from 'lodash';
import {rollingCorrelation} from "../../../utils/math";
import Placeholder from "../../../components/Placeholder";
import {VisibilityRounded, VisibilityOffRounded} from "@mui/icons-material";
import {useScreenHeight} from "../../../hooks/useScreen";
import PlotlyStockChart  from "../../../charts/Plotly/PlotlyStockChart";
import { anystockPercentagePlotConfig } from "../../../charts/Anychart/applyAnychartStyles";
import {getSectorCode} from "../../../utils/abbreviations";
const { TabPane } = Tabs;

const { useBreakpoint } = Grid;
const reformatBenchmarkSeries = (data) => {
    return data.map(x => [x.timestamp, x['adj close']]);
};

const SectorTrackingToolbar = ({ tickerLimit, setTickerLimit, showPortfolio, setShowPortfolio }) => {
    // Use useCallback to ensure debounce function is memoized and not recreated on every render
    const debouncedSetTickerLimit = useCallback(
        debounce((value) => {
            setTickerLimit(value);
        }, 300), // Adjust the debounce delay (in milliseconds) as needed
        []
    );

    const handleSliderChange = (value) => {
        debouncedSetTickerLimit(value);
    };

    return (
        <Space>
            <Button onClick={()=>setShowPortfolio(!showPortfolio)} startIcon={showPortfolio ? <VisibilityOffRounded/>:<VisibilityRounded/>}>
                {showPortfolio ? 'Hide' : 'Show'}
            </Button>
            <Space direction="horizontal" align="center" style={{ padding: '10px', width: '100%' }}>
                <Typography.Text strong>#</Typography.Text>
                <Slider
                    min={1}
                    max={30}
                    value={tickerLimit}
                    onChange={handleSliderChange}
                    style={{ width: 100 }}
                />
                <Typography.Text>{tickerLimit}</Typography.Text>
            </Space>

        </Space>
    );
};

const SectorTracking = ({ allSectors, getSectorTickers }) => {
    const dispatch = useDispatch();

    const [activeTab, setActiveTab] = useState('init');
    const [selectedTickers, setSelectedTickers] = useState(null);
    const [selectedBenchmark, setSelectedBenchmark] = useState(null);
    const [startDate, setStartDate] = useState('2022-01-01');
    const [tickerLimit, setTickerLimit] = useState(10);
    const [showPortfolio, setShowPortfolio] = useState(true);

    const screenHeight = useScreenHeight();
    const bp = useBreakpoint();

    const blendedSeries = useSelector(state => state.timeSeries.blendedSeries);
    const timeSeriesData = useSelector(state => selectTimeSeriesData(state, selectedBenchmark));
    const lastBlended = blendedSeries && blendedSeries[blendedSeries.length - 1];
    const blendedPortfolio = lastBlended && lastBlended?.mapping;
    const { tickers } = useTickerSearchIndex(blendedPortfolio ? Object.keys(blendedPortfolio) : ['spy']);
    const portfolioTickers = blendedPortfolio && (
        Object.entries(blendedPortfolio).map(([k, v]) => ({ k, v })).sort((a, b)=>b.v - a.v)
    )

    const sectorCounts = useMemo(()=>{
        return allSectors.reduce((acc, sector)=>{
            acc[sector] = getSectorTickers(sector).length;
            return acc;
        }, {});
    })

    const handleTabChange = (sector) => {
        setActiveTab(sector);
        const sectorTickers = getSectorTickers(sector, tickerLimit);
        setSelectedTickers(sectorTickers);
        const code = getSectorCode(sector);
        setSelectedBenchmark(code || 'spy')
    };

    useEffect(()=>{
        if (!selectedBenchmark) return;
        dispatch(fetchTimeSeriesData({ symbols: [selectedBenchmark], startDate }));

    }, [selectedBenchmark]);

    useEffect(() => {
        if (!selectedTickers) return;
        const sectorTickers = getSectorTickers(activeTab, tickerLimit);
        setSelectedTickers(sectorTickers);
    }, [tickerLimit]);

    useEffect(() => {
        if (!selectedTickers) return;
        const mapping = selectedTickers.map(ticker => ({ k: ticker.symbol, v: ticker.wgt }));
        dispatch(fetchBlendedTimeseriesV2({ mapping, startDate }));
    }, [selectedTickers]);

    const columns = portfolioTickers && [
        {
            title: 'Ticker',
            dataIndex: 'symbol',
            key: 'symbol',
            render: (_, record) => (
                <TickerChip
                    ticker={tickers.find(x => x.symbol === record.k)}
                    imgSize={24}
                    width={180}
                    titleSize={12}
                    textSize={10}
                    maxLength={20}
                />
            ),
            width: 180
        },
        {
            title: 'Weight',
            dataIndex: 'wgt',
            key: 'wgt',
            render: (_, record) => (
                <Typography.Text>{stringMixin.pct(record.v)}</Typography.Text>
            ),
            width: 80
        }
    ];

    const chartSeriesData = lastBlended && timeSeriesData && [
        {
            name: `${activeTab} Blend`,
            data: lastBlended?.series,
            stroke: '#0097fb'
        },
        {
            name: selectedBenchmark.toUpperCase(),
            data: reformatBenchmarkSeries(timeSeriesData),
            stroke: '#6b757a'
        }
    ];

    const SideColumn = ()=>{
        return (
            <Card bordered={false}>
                <SectorTrackingToolbar tickerLimit={tickerLimit}
                                       setTickerLimit={setTickerLimit}
                                       showPortfolio={showPortfolio}
                                       setShowPortfolio={setShowPortfolio}
                />
                <Table
                    dataSource={portfolioTickers}
                    columns={columns}
                    pagination={false}
                    scroll={{ y: '80vh' }}
                />
            </Card>
        )
    }
    return (
        <div style={{height: screenHeight - 200}}>
            <Tabs activeKey={activeTab} onChange={handleTabChange} type="card" tabPosition={'top'}>
                {allSectors.filter(x=>!!x&&sectorCounts?.[x] > 5).map(sector => (
                    <TabPane tab={`${stringMixin.fmt(sector)} (${sectorCounts?.[sector] ?? 0})`} key={sector}>
                        <Row wrap={false} gutter={16}>
                            {showPortfolio && columns && bp.xl && (
                                <Col xl={6} className={'border-r-2 border-gray-100'}>
                                    <SideColumn />
                                </Col>
                            )}
                            <Col xs={24} xl={18}>
                                <Card bordered={false}>
                                    {activeTab === sector && lastBlended && timeSeriesData && (
                                        <div style={{ height: '75vh', width: '100%' }}>
                                            <StockChart
                                                dataSeries={[
                                                    {
                                                        plot: 0,
                                                        data: chartSeriesData,
                                                        ...anystockPercentagePlotConfig
                                                    }
                                                ]}
                                            />
                                        </div>
                                    )}
                                </Card>
                            </Col>
                        </Row>
                    </TabPane>
                ))}
                <TabPane key={'init'}>
                    <Card bordered={false}>
                        <Placeholder height={200} text={'Click a Sector to View its Tracking'} imgUrl={'https://storage.googleapis.com/finiat-web-static-resources/appIcons/files-split.webp'} />
                    </Card>
                </TabPane>
            </Tabs>
        </div>
    );
};

export default SectorTracking;
